import React from "react";
import { Tag, cn } from "@jem-open/jem-ui";
import { PreviewParams } from "./types";
import PreviewHeader from "./PreviewHeader";
import SummaryTile from "./SummaryTile";
import { Td, Th } from "./tableHelpers";
import { PostingSheetPreviewData } from "../../services/model/ReportPreviewModels";

interface PostingSheetPreviewProps {
  params: PreviewParams;
  data: PostingSheetPreviewData;
  onDownload: () => void;
}

function statusTagVariant(status: string): "success" | "error" | "drafted" {
  if (status === "P") return "success";
  if (status === "A") return "error";
  return "drafted";
}

const PostingSheetPreview = ({
  params,
  data,
  onDownload,
}: PostingSheetPreviewProps): JSX.Element => (
  <>
    <PreviewHeader
      title="Posting Sheet"
      subtitle={`Daily roster — ${data.shift_label}`}
      params={params}
      onDownload={onDownload}
    />

    <div className="grid grid-cols-3 gap-sm mb-lg">
      <SummaryTile
        label="Present"
        value={String(data.present_count)}
        unit="on duty"
      />
      <SummaryTile
        label="Absent"
        value={String(data.absent_count)}
        unit="missing"
        flag={data.absent_count > 0}
      />
      <SummaryTile label="N/A" value={String(data.na_count)} unit="other" />
    </div>

    <h2 className="text-sm font-semibold text-greyscale-text-title mb-xs">
      Guard roster
    </h2>
    <div className="overflow-x-auto rounded-lg border border-greyscale-border">
      <table className="w-full text-xs">
        <thead className="bg-neutral-50 border-b border-greyscale-border">
          <tr>
            <Th>Pers No</Th>
            <Th>Name</Th>
            <Th>Shift Hrs</Th>
            <Th>Shift Time</Th>
            <Th>Status</Th>
            <Th>Replaced By</Th>
            <Th>Time On</Th>
            <Th>Time Off</Th>
          </tr>
        </thead>
        <tbody>
          {data.rows.length === 0 ? (
            <tr>
              <td
                colSpan={8}
                className="px-sm py-md text-center text-sm text-greyscale-text-caption"
              >
                No roster entries for this date and shift.
              </td>
            </tr>
          ) : (
            data.rows.map((r) => (
              <tr
                key={`${r.pers_no ?? r.name}-${r.shift_time}-${r.status_code}-${
                  r.time_on
                }-${r.time_off}-${r.replaced_by}`}
                className={cn(
                  "border-b border-greyscale-border last:border-0",
                  r.status_code === "A" && "bg-red-50"
                )}
              >
                <Td mono>{r.pers_no ?? "—"}</Td>
                <Td className="font-semibold">{r.name}</Td>
                <Td mono>{r.shift_hrs}</Td>
                <Td mono>{r.shift_time}</Td>
                <Td>
                  <Tag variant={statusTagVariant(r.status_code)} size="sm">
                    {r.status_code}
                  </Tag>
                </Td>
                <Td>{r.replaced_by}</Td>
                <Td mono>{r.time_on}</Td>
                <Td mono>{r.time_off}</Td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>

    <div className="mt-lg grid grid-cols-1 sm:grid-cols-2 gap-md">
      <div>
        <div className="border-b border-dashed border-greyscale-text-caption h-12 mb-xs" />
        <p className="text-xs uppercase tracking-wide text-greyscale-text-caption">
          Supervisor signature
        </p>
      </div>
      <div>
        <div className="border-b border-dashed border-greyscale-text-caption h-12 mb-xs" />
        <p className="text-xs uppercase tracking-wide text-greyscale-text-caption">
          Control room sign-off
        </p>
      </div>
    </div>
  </>
);

export default PostingSheetPreview;
